/**
 * Easter Egg Registry — Djay Mascot Domain
 *
 * DDD co-locatie: Easter eggs zijn onderdeel van het mascotte-domein.
 * Ze gebruiken bestaande DjayEmotion values en worden getriggerd door
 * gebruikersinteracties met Djay of de app.
 *
 * Elk Easter egg heeft:
 * - Een uniek ID voor persistentie
 * - Een trigger-conditie (pure function)
 * - Een Djay-reactie (emotion + message)
 * - Optioneel: een reward (variant unlock, sound, etc.)
 *
 * Usage:
 *   import { EASTER_EGGS, checkTimeBasedEgg } from './easterEggs'
 */

import type { DjayEmotion } from './DjayAvatar'

// ── Types ────────────────────────────────────────────────────

export type EasterEggId =
  | 'rapid-tap'
  | 'konami-code'
  | 'nacht-uil'
  | 'vroege-vogel'
  | 'long-press-goud'

export interface EasterEggReaction {
  text: string
  emotion: DjayEmotion
}

export interface EasterEggDefinition {
  id: EasterEggId
  /** Display name (Dutch) */
  name: string
  /** Short description for discovery log */
  description: string
  /** The Djay reaction when triggered */
  reaction: EasterEggReaction
  /** Can this egg trigger more than once? */
  repeatable: boolean
  /** Optional: sound to play (key from soundManager) */
  sound?: 'playAchievement' | 'playDjaySpeak' | 'playLevelUp'
}

// ── Registry ─────────────────────────────────────────────────

export const EASTER_EGGS: Record<EasterEggId, EasterEggDefinition> = {
  'rapid-tap': {
    id: 'rapid-tap',
    name: 'Kietelaar',
    description: 'Tik 5x snel op Djay',
    reaction: {
      text: 'Hahaha, dat kietelt! 🤣 Stop, stop!',
      emotion: 'surprised',
    },
    repeatable: true,
    sound: 'playDjaySpeak',
  },
  'konami-code': {
    id: 'konami-code',
    name: 'Geheime Code',
    description: 'Voer de Konami Code in',
    reaction: {
      text: 'Geheim gevonden! Je bent een echte gamer! 🎮✨',
      emotion: 'celebrating',
    },
    repeatable: false,
    sound: 'playAchievement',
  },
  'nacht-uil': {
    id: 'nacht-uil',
    name: 'Nacht-Uil',
    description: 'Oefen na 22:00',
    reaction: {
      text: 'Jij bent een echte nacht-uil! 🦉 Vergeet niet om te slapen, hè!',
      emotion: 'sleeping',
    },
    repeatable: true,
    sound: 'playDjaySpeak',
  },
  'vroege-vogel': {
    id: 'vroege-vogel',
    name: 'Vroege Vogel',
    description: 'Oefen voor 07:00',
    reaction: {
      text: 'Wauw, zo vroeg al?! 🐦 De vroege vogel vangt de worm!',
      emotion: 'surprised',
    },
    repeatable: true,
    sound: 'playDjaySpeak',
  },
  'long-press-goud': {
    id: 'long-press-goud',
    name: 'DJ Goud',
    description: 'Houd Djay lang ingedrukt',
    reaction: {
      text: 'Geheim ontdekt! DJ Goud ontgrendeld! ✨',
      emotion: 'celebrating',
    },
    repeatable: false,
    sound: 'playAchievement',
  },
}

// ── Trigger Helpers ──────────────────────────────────────────

/** Check if a time-based Easter egg should trigger */
export function checkTimeBasedEgg(): EasterEggId | null {
  const hour = new Date().getHours()
  if (hour >= 22 || hour < 5) return 'nacht-uil'
  if (hour >= 5 && hour < 7) return 'vroege-vogel'
  return null
}

/** Detect rapid taps: returns true if threshold reached */
export function createRapidTapDetector(
  threshold = 5,
  windowMs = 2000,
): { recordTap: () => boolean; reset: () => void } {
  let taps: number[] = []

  return {
    recordTap(): boolean {
      const now = Date.now()
      taps.push(now)
      // Remove taps outside the window
      taps = taps.filter((t) => now - t < windowMs)
      return taps.length >= threshold
    },
    reset() {
      taps = []
    },
  }
}

/** Konami code sequence detector */
export function createKonamiDetector(): {
  handleKey: (key: string) => boolean
  reset: () => void
} {
  const SEQUENCE = [
    'ArrowUp',
    'ArrowUp',
    'ArrowDown',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowLeft',
    'ArrowRight',
    'b',
    'a',
  ]
  let position = 0

  return {
    handleKey(key: string): boolean {
      if (key === SEQUENCE[position]) {
        position++
        if (position === SEQUENCE.length) {
          position = 0
          return true
        }
      } else {
        position = key === SEQUENCE[0] ? 1 : 0
      }
      return false
    },
    reset() {
      position = 0
    },
  }
}
